import type { OverlaySlide } from '~/ui-rocco/pages/detail-shared';
import { readColumnImage } from './collect-project-images';

/**
 * Every film in a project's gallery, for the contact sheet.
 *
 * Separate from `collectProjectImages` rather than folded into it: that walk
 * feeds the lookbook PDF and the hire cards as well as the grid, and a poster
 * frame handed to either of those reads as a photograph that will not play.
 * This one is only ever asked for by the surface that can draw a player.
 *
 * Each film records how many photographs preceded it, so the caller can put it
 * back where the page has it rather than after everything else.
 */
export interface ProjectVideoSlide extends OverlaySlide {
    /** The film itself. The caller builds the player; `src` is its poster. */
    videoUrl: string;
    /** Photographs before this film in the walk — where it belongs in the grid. */
    insertAt: number;
}

const readVideo = (column: unknown) => {
    const video = (column as Record<string, unknown> | null)?.video;
    if (!video || typeof video !== 'object') return null;
    const record = video as Record<string, unknown>;
    const url = record.videoURL;
    if (typeof url !== 'string' || !url) return null;
    const poster =
        (typeof record.coverImage === 'string' && record.coverImage) ||
        (typeof record.coverImageThumbnail === 'string' &&
            record.coverImageThumbnail) ||
        '';
    return {
        url,
        poster,
        title: typeof record.title === 'string' ? record.title : undefined,
    };
};

export const collectProjectVideos = (
    modules: unknown,
    alt: string,
): ProjectVideoSlide[] => {
    if (!Array.isArray(modules)) return [];

    const seen = new Set<string>();
    // The photographs counted the way the image walk collects them, repeats
    // folded together, so `insertAt` indexes the slides it will be spliced
    // into rather than the columns they were read from.
    const seenImages = new Set<string>();
    const videos: ProjectVideoSlide[] = [];
    let images = 0;

    for (const module of modules) {
        const columns = (module as Record<string, unknown> | null)?.columns;
        if (!Array.isArray(columns)) continue;
        for (const column of columns) {
            const image = readColumnImage(column);
            if (image) {
                if (!seenImages.has(image.key)) {
                    seenImages.add(image.key);
                    images += 1;
                }
                continue;
            }
            const video = readVideo(column);
            // A film with no poster has nothing to draw in a contact sheet,
            // and the sheet is the only reason this walk exists.
            if (!video || !video.poster) continue;
            if (seen.has(video.url)) continue;
            seen.add(video.url);
            videos.push({
                src: video.poster,
                alt: video.title || alt,
                videoUrl: video.url,
                insertAt: images,
            });
        }
    }

    return videos;
};
